import { formatScore, type ByeKind, type Entrant, type Tournament } from '@ctd/core';
import { Fragment, useId, useState, type ChangeEvent, type FormEvent } from 'react';
import { useNotify } from '../components/notices';
import { PageHeading, SelectField, TableRegion, TextField } from '../components/ui';
import { useDispatch, useSnapshot } from '../context';
import { errorText } from '../lib/download';
import {
  TITLES,
  addPlayerEvent,
  emptyPlayerForm,
  formFromPlayer,
  isSeeded,
  playerInfoFromForm,
  playerPatch,
  previewCsv,
  seedOrder,
  type PlayerForm,
  type PlayerFormErrors,
} from '../lib/players';
import { REQUESTABLE_BYES, byeLabel } from '../lib/results';
import { scoresBefore } from '../lib/rounds';

export function PlayersTab() {
  const { tournament: t } = useSnapshot();
  const dispatch = useDispatch();
  const [editing, setEditing] = useState<string | null>(null);
  const [byesOpen, setByesOpen] = useState<string | null>(null);
  const players = [...t.players].sort((a, b) => a.pairingNumber - b.pairingNumber);
  const editingPlayer = players.find((p) => p.id === editing) ?? null;
  const started = t.rounds.length > 0;
  const scores = scoresBefore(t, t.rounds.length + 1);

  function seed(): void {
    dispatch(
      { type: 'PlayersRenumbered', order: seedOrder(t.players) },
      'Players numbered by rating.',
    );
  }

  function remove(p: Entrant): void {
    if (!window.confirm(`Remove ${p.name} from the tournament?`)) return;
    if (editing === p.id) setEditing(null);
    dispatch({ type: 'PlayerRemoved', id: p.id }, `Removed ${p.name}.`);
  }

  function withdraw(p: Entrant): void {
    const after = t.rounds.length;
    dispatch(
      { type: 'PlayerWithdrawn', id: p.id, afterRound: after },
      after === 0
        ? `${p.name} withdrawn before round 1.`
        : `${p.name} withdrawn after round ${after}; not paired from round ${after + 1}.`,
    );
  }

  return (
    <section>
      <div className="page-head">
        <PageHeading>Players ({players.length})</PageHeading>
        <div className="actions">
          <button
            type="button"
            className="button"
            onClick={seed}
            disabled={started || players.length < 2 || isSeeded(t.players)}
            aria-describedby="seed-hint"
          >
            Number by rating
          </button>
        </div>
      </div>
      <p id="seed-hint" className="hint">
        {started
          ? 'Pairing numbers are fixed once round 1 is paired.'
          : 'Number by rating gives pairing number 1 to the highest rated player (do this before pairing round 1).'}
      </p>

      <PlayerEditor key={editing ?? 'new'} player={editingPlayer} onDone={() => setEditing(null)} />

      <CsvImport />

      {players.length === 0 ? (
        <p className="empty">No players yet. Add them with the form above or import a CSV file.</p>
      ) : (
        <TableRegion label="Players">
          <table className="data-table players-table">
            <thead>
              <tr>
                <th scope="col" className="num">
                  No.
                </th>
                <th scope="col">Name</th>
                <th scope="col" className="num">
                  Rating
                </th>
                <th scope="col">Fed</th>
                <th scope="col">Club</th>
                {started && (
                  <th scope="col" className="num">
                    Pts
                  </th>
                )}
                <th scope="col">Status</th>
                <th scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {players.map((p) => (
                <Fragment key={p.id}>
                  <tr className={p.withdrawnAfterRound !== null ? 'withdrawn' : undefined}>
                    <td className="num">{p.pairingNumber}</td>
                    <th scope="row">
                      {p.title ? <abbr className="title">{p.title}</abbr> : null} {p.name}
                    </th>
                    <td className="num">{p.rating || '–'}</td>
                    <td>{p.federation || ''}</td>
                    <td>{p.club || ''}</td>
                    {started && <td className="num">{formatScore(scores.get(p.id) ?? 0)}</td>}
                    <td>
                      <PlayerStatus p={p} />
                    </td>
                    <td>
                      <div className="row-actions">
                        <button
                          type="button"
                          className="button small"
                          aria-label={`Edit ${p.name}`}
                          onClick={() => setEditing(p.id)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="button small"
                          aria-expanded={byesOpen === p.id}
                          aria-controls={`byes-${p.id}`}
                          aria-label={`Byes for ${p.name}`}
                          onClick={() => setByesOpen(byesOpen === p.id ? null : p.id)}
                        >
                          Byes
                        </button>
                        {p.withdrawnAfterRound === null ? (
                          <button
                            type="button"
                            className="button small"
                            aria-label={`Withdraw ${p.name}`}
                            onClick={() => withdraw(p)}
                          >
                            Withdraw
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="button small"
                            aria-label={`Reinstate ${p.name}`}
                            onClick={() =>
                              dispatch(
                                { type: 'PlayerReinstated', id: p.id },
                                `${p.name} reinstated.`,
                              )
                            }
                          >
                            Reinstate
                          </button>
                        )}
                        {!started && (
                          <button
                            type="button"
                            className="button small danger"
                            aria-label={`Remove ${p.name}`}
                            onClick={() => remove(p)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {byesOpen === p.id && (
                    <tr className="detail-row">
                      <td colSpan={started ? 8 : 7} id={`byes-${p.id}`}>
                        <ByePanel t={t} p={p} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </TableRegion>
      )}
    </section>
  );
}

function PlayerStatus({ p }: { p: Entrant }) {
  const parts: string[] = [];
  if (p.withdrawnAfterRound !== null) parts.push(`Withdrawn after round ${p.withdrawnAfterRound}`);
  for (const b of p.byeRequests) {
    parts.push(`R${b.round}: ${byeLabel(b.kind).toLowerCase()}`);
  }
  return parts.length === 0 ? <span className="muted">Active</span> : <>{parts.join('; ')}</>;
}

function PlayerEditor({ player, onDone }: { player: Entrant | null; onDone: () => void }) {
  const dispatch = useDispatch();
  const notify = useNotify();
  const [form, setForm] = useState<PlayerForm>(
    player === null ? emptyPlayerForm() : formFromPlayer(player),
  );
  const [errors, setErrors] = useState<PlayerFormErrors>({});
  const set =
    <K extends keyof PlayerForm>(k: K) =>
    (v: string) =>
      setForm((f) => ({ ...f, [k]: v as PlayerForm[K] }));

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const formEl = e.currentTarget;
    const { info, errors: errs } = playerInfoFromForm(form);
    setErrors(errs);
    if (info === null) {
      formEl.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
      return;
    }
    if (player === null) {
      if (dispatch(addPlayerEvent(info), `Added ${info.name}.`)) {
        setForm(emptyPlayerForm());
        setErrors({});
        // Ready for the next player.
        formEl.querySelector<HTMLInputElement>('input')?.focus();
      }
      return;
    }
    const patch = playerPatch(player, info);
    if (Object.keys(patch).length === 0) {
      notify('No changes to save.');
      onDone();
      return;
    }
    if (dispatch({ type: 'PlayerUpdated', id: player.id, patch }, `Saved ${info.name}.`)) onDone();
  }

  return (
    <form className="panel player-form" onSubmit={submit} noValidate>
      <h3>{player === null ? 'Add a player' : `Edit ${player.name}`}</h3>
      <div className="form-grid compact">
        <TextField
          className="span-2"
          label="Name"
          value={form.name}
          onValue={set('name')}
          error={errors.name}
          autoComplete="off"
          hint="Surname, first name"
          required
        />
        <TextField
          label="Rating"
          inputMode="numeric"
          value={form.rating}
          onValue={set('rating')}
          error={errors.rating}
          autoComplete="off"
        />
        <SelectField
          label="Title"
          value={form.title}
          onValue={set('title')}
          options={[{ value: '', label: 'None' }, ...TITLES.map((x) => ({ value: x, label: x }))]}
        />
        <TextField
          label="Federation"
          value={form.federation}
          onValue={set('federation')}
          error={errors.federation}
          maxLength={3}
          autoComplete="off"
        />
        <TextField
          label="FIDE ID"
          inputMode="numeric"
          value={form.fideId}
          onValue={set('fideId')}
          error={errors.fideId}
          autoComplete="off"
        />
        <TextField
          label="Birth date"
          value={form.birthDate}
          onValue={set('birthDate')}
          placeholder="YYYY or YYYY-MM-DD"
          autoComplete="off"
        />
        <SelectField
          label="Sex"
          value={form.sex}
          onValue={set('sex')}
          options={[
            { value: '', label: 'Not given' },
            { value: 'f', label: 'Female' },
            { value: 'm', label: 'Male' },
          ]}
        />
        <TextField label="Club" value={form.club} onValue={set('club')} autoComplete="off" />
      </div>
      <div className="form-actions">
        <button type="submit" className="button primary">
          {player === null ? 'Add player' : 'Save changes'}
        </button>
        {player !== null && (
          <button type="button" className="button" onClick={onDone}>
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

const CSV_EXAMPLE = 'name,rating,federation,title\n"Carlsen, Magnus",2830,NOR,GM';

function CsvImport() {
  const dispatch = useDispatch();
  const notify = useNotify();
  const [text, setText] = useState('');
  const textId = useId();
  const preview = previewCsv(text);

  async function onFile(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.currentTarget.files?.[0];
    e.currentTarget.value = '';
    if (file === undefined) return;
    try {
      setText(await file.text());
    } catch (err) {
      notify(`Could not read ${file.name}: ${errorText(err)}`, 'error');
    }
  }

  function importAll(): void {
    let added = 0;
    for (const info of preview.players) {
      if (!dispatch(addPlayerEvent(info))) break;
      added++;
    }
    if (added > 0) {
      notify(`Imported ${added} player${added > 1 ? 's' : ''}.`);
      setText('');
    }
  }

  return (
    <details className="panel csv-import">
      <summary>Import players from CSV</summary>
      <p className="hint">
        Paste CSV text or choose a file. The first line is a header; recognised columns include name
        (or first and last), rating, title, federation, fide_id, birth, sex, club. Comma, semicolon
        and tab separators work.
      </p>
      <label htmlFor={textId}>CSV text</label>
      <textarea
        id={textId}
        rows={5}
        value={text}
        placeholder={CSV_EXAMPLE}
        onChange={(e) => setText(e.target.value)}
        spellCheck={false}
      />
      <div className="form-actions">
        <label className="button file-button">
          Choose CSV file…
          <input
            type="file"
            className="visually-hidden"
            accept=".csv,.tsv,.txt,text/csv,text/plain"
            onChange={(e) => void onFile(e)}
          />
        </label>
        <button
          type="button"
          className="button primary"
          disabled={preview.players.length === 0}
          onClick={importAll}
        >
          {preview.players.length > 0
            ? `Import ${preview.players.length} player${preview.players.length > 1 ? 's' : ''}`
            : 'Import'}
        </button>
      </div>
      {preview.error !== null && (
        <p className="field-error" role="alert">
          {preview.error}
        </p>
      )}
    </details>
  );
}

function ByePanel({ t, p }: { t: Tournament; p: Entrant }) {
  const dispatch = useDispatch();
  const firstOpen = t.rounds.length + 1;
  const rounds: number[] = [];
  for (let r = firstOpen; r <= t.settings.rounds; r++) rounds.push(r);
  const [round, setRound] = useState(String(rounds[0] ?? ''));
  const [kind, setKind] = useState<ByeKind>('H');

  function add(e: FormEvent): void {
    e.preventDefault();
    const r = Number(round);
    if (!Number.isInteger(r)) return;
    dispatch(
      { type: 'ByeRequested', id: p.id, round: r, kind },
      `${byeLabel(kind)} for ${p.name} in round ${r}.`,
    );
  }

  return (
    <div className="bye-panel">
      <h3>Requested byes for {p.name}</h3>
      {p.byeRequests.length === 0 ? (
        <p className="muted">None.</p>
      ) : (
        <ul className="bye-list">
          {p.byeRequests.map((b) => (
            <li key={b.round}>
              Round {b.round}: {byeLabel(b.kind).toLowerCase()}{' '}
              {b.round > t.rounds.length && (
                <button
                  type="button"
                  className="button small"
                  onClick={() =>
                    dispatch(
                      { type: 'ByeCancelled', id: p.id, round: b.round },
                      `Cancelled the round ${b.round} bye for ${p.name}.`,
                    )
                  }
                >
                  Cancel
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {rounds.length === 0 ? (
        <p className="muted">Every round is paired.</p>
      ) : (
        <form className="inline-form" onSubmit={add}>
          <SelectField
            label="Round"
            value={round}
            onValue={setRound}
            options={rounds.map((r) => ({ value: String(r), label: String(r) }))}
          />
          <SelectField
            label="Kind"
            value={kind}
            onValue={(v) => setKind(v as ByeKind)}
            options={REQUESTABLE_BYES.map((b) => ({ value: b.kind, label: b.label }))}
          />
          <button type="submit" className="button">
            Request bye
          </button>
        </form>
      )}
    </div>
  );
}
